import { put, call, takeLatest, select } from 'redux-saga/effects';
import _get from 'lodash/get';
import ActionTypes from '../constants/ActionTypes';
import { baseUrl } from '../constants/AppConstants';
import {
  getUserTypeApi,
  getRecruiterDetails,
  getHackerDetails,
  getServerTimeAndTrackingId,
} from '../api/test';
import { progressStart, progressStop } from '../actions';
import { showErrorToast } from '../actions/toast';
import { getApiErrorData } from '../utils/helperFunction';
import { proctoringUBALogger } from '../utils/trackingUtil';
import {
  ONE_MINUTE_IN_MILI_SECONDS,
  AUTO_SUBMIT_REASONS,
} from '../constants/Constants';

function* getUserType() {
  try {
    yield put(progressStart());
    const resp = yield call(getUserTypeApi);
    if (!_get(resp, 'data.user_id'))
      window.location.replace(`${baseUrl}/accounts/logout`);
    else {
      yield put({
        type: `${ActionTypes.GET_USER_TYPE}_SUCCESS`,
        payload: resp.data,
      });
    }
  } catch (e) {
    window.location.replace(`${baseUrl}/accounts/logout`);
    const { toast } = yield select(state => state.appStatus);
    const errorInfo = getApiErrorData(e, 'IDT');
    if (errorInfo && errorInfo.message && !toast?.apiErrorInfo) {
      yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
    }
    yield put({
      type: `${ActionTypes.AUTH}_FAIL`,
      error: e,
    });
  } finally {
    yield put(progressStop());
  }
}
function* getRecruiter() {
  try {
    yield put(progressStart());
    const { user_id } = yield select(state => state.auth.initUserDetails);
    const resp = yield call(getRecruiterDetails, user_id);
    yield put({
      type: `${ActionTypes.GET_RECRUITER}_SUCCESS`,
      payload: resp.data,
    });
  } catch (e) {
    const { toast } = yield select(state => state.appStatus);
    const errorInfo = getApiErrorData(e, 'REC');
    if (errorInfo && errorInfo.message && !toast?.apiErrorInfo) {
      yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
    }
  } finally {
    yield put(progressStop());
  }
}
function* getHacker() {
  try {
    yield put(progressStart());
    const { user_id } = yield select(state => state.auth.initUserDetails);
    const resp = yield call(getHackerDetails, user_id);
    yield put({
      type: `${ActionTypes.GET_HACKER}_SUCCESS`,
      payload: resp.data,
    });
  } catch (e) {
    const { toast } = yield select(state => state.appStatus);
    const errorInfo = getApiErrorData(e, 'GHD');
    if (errorInfo && errorInfo.message && !toast?.apiErrorInfo) {
      yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
    }
  } finally {
    yield put(progressStop());
  }
}
export function* getServerTimeAndTrackingIds({ isInitialCall, isLastCall }) {
  try {
    if (isInitialCall) yield put(progressStart());
    const requestTime = new Date().getTime();
    const { data } = yield call(getServerTimeAndTrackingId);
    const serverTime = data.server_time;
    if (serverTime) {
      const prevOffsetTime = yield select(state => state.appStatus.offsetTime);
      let offsetTime = new Date(serverTime).getTime() - Date.now();
      const responseTime = new Date().getTime();
      const processLatencyTime = responseTime - requestTime;
      offsetTime += processLatencyTime;
      const offsetDiff = prevOffsetTime - offsetTime;
      const isSystemTimeMismatchEnabled = data.is_system_time_mismatch_enabled;
      const isSystemTimeMismatch =
        Math.abs(offsetTime) > ONE_MINUTE_IN_MILI_SECONDS &&
        Math.abs(offsetDiff) > ONE_MINUTE_IN_MILI_SECONDS * 5;

      if (
        !isInitialCall &&
        !isSystemTimeMismatchEnabled &&
        isSystemTimeMismatch
      ) {
        // it is added temporarily for analyzing
        proctoringUBALogger('info:system-time-mismatch-offset', offsetTime);
      }

      if (isLastCall && isSystemTimeMismatch) {
        yield put({
          type: ActionTypes.SET_AUTO_SUBMIT_REASON,
          reason: AUTO_SUBMIT_REASONS.SYSTEM_TIME_MISMATCH,
        });
      }

      yield put({
        type: `${ActionTypes.GET_SERVER_TIME_AND_TRACKING_ID}_SUCCESS`,
        payload: {
          serverTime,
          offsetTime,
          isInitialCall,
          isSystemTimeMismatch,
          isSystemTimeMismatchEnabled,
        },
      });
    } else {
      proctoringUBALogger('error', 'failed to get server time');
    }
  } catch (e) {
    if (isInitialCall) {
      const { toast } = yield select(state => state.appStatus);
      const errorInfo = getApiErrorData(e, 'GST');
      if (errorInfo && errorInfo.message && !toast?.apiErrorInfo) {
        yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
      }
    }
  } finally {
    if (isInitialCall) yield put(progressStop());
  }
}
export default function* loadAuthSaga() {
  yield takeLatest(ActionTypes.GET_USER_TYPE, getUserType);
  yield takeLatest(ActionTypes.GET_RECRUITER, getRecruiter);
  yield takeLatest(ActionTypes.GET_HACKER, getHacker);
  yield takeLatest(
    ActionTypes.GET_SERVER_TIME_AND_TRACKING_ID,
    getServerTimeAndTrackingIds
  );
}
